import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  Users, Shield, ShieldOff, Loader2, ArrowLeft, 
  Briefcase, Calendar, Mail, ChevronDown, ChevronUp, TrendingUp 
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

const Admin = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [allPortfolios, setAllPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedUser, setExpandedUser] = useState(null);

  useEffect(() => {
    // Check admin access
    if (user && !user.is_admin) {
      toast.error(t('admin.adminRequired'));
      navigate("/");
      return;
    }
    
    fetchData();
  }, [user, navigate, t]);

  const fetchData = async () => {
    try {
      const [usersData, portfoliosData] = await Promise.all([
        api.getUsers(),
        api.getAllPortfolios()
      ]);
      setUsers(usersData);
      setAllPortfolios(portfoliosData);
    } catch (error) {
      toast.error(t('admin.failedLoadData'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAdminRole = async (userId, currentIsAdmin) => {
    // Prevent removing own admin role
    if (userId === user.user_id && currentIsAdmin) {
      toast.error(t('admin.cannotRemoveOwnAdmin'));
      return;
    }

    try {
      await api.updateUser(userId, { is_admin: !currentIsAdmin });
      setUsers(users.map(u => 
        u.user_id === userId ? { ...u, is_admin: !currentIsAdmin } : u
      ));
      toast.success(currentIsAdmin ? t('admin.adminRevoked') : t('admin.adminGranted'));
    } catch (error) {
      toast.error(t('admin.failedUpdateUser'));
    }
  };

  const getUserPortfolios = (userId) => {
    return allPortfolios.filter(p => p.user_id === userId);
  };

  const getUserAssetCount = (userId) => {
    return getUserPortfolios(userId).reduce((total, p) => total + (p.assets?.length || 0), 0);
  };

  const getTotalAssets = () => {
    return allPortfolios.reduce((total, p) => total + (p.assets?.length || 0), 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F766E]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/")}
                className="text-slate-600"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Shield className="w-5 h-5 text-amber-600" />
                </div>
                <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                  {t('admin.title')}
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-teal-50 rounded-xl">
                  <Users className="w-6 h-6 text-[#0F766E]" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t('admin.totalUsers')}</p>
                  <p className="text-2xl font-semibold">{users.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-amber-50 rounded-xl">
                  <Shield className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t('admin.adminUsers')}</p>
                  <p className="text-2xl font-semibold">{users.filter(u => u.is_admin).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 rounded-xl">
                  <Briefcase className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t('admin.totalPortfolios')}</p>
                  <p className="text-2xl font-semibold">{allPortfolios.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-50 rounded-xl">
                  <TrendingUp className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">{t('admin.totalAssets')}</p>
                  <p className="text-2xl font-semibold">{getTotalAssets()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Users List */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>{t('admin.userManagement')}</CardTitle>
            <CardDescription>{t('admin.userManagementDesc')}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {users.map((u, index) => (
                <motion.div
                  key={u.user_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div 
                    className={`border border-slate-200 rounded-lg overflow-hidden transition-all ${
                      expandedUser === u.user_id ? 'ring-2 ring-teal-200' : ''
                    }`}
                  >
                    {/* User Row */}
                    <div 
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50"
                      onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}
                    >
                      <div className="flex items-center gap-4">
                        {u.picture ? (
                          <img 
                            src={u.picture} 
                            alt={u.name} 
                            className="w-10 h-10 rounded-full"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center">
                            <span className="text-teal-700 font-medium">
                              {u.name?.charAt(0)?.toUpperCase() || 'U'}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{u.name}</span>
                            {u.is_admin && (
                              <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                                {t('admin.admin')}
                              </Badge>
                            )}
                            {u.user_id === user.user_id && (
                              <Badge variant="outline" className="text-slate-500">
                                {t('admin.you')}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-slate-500">
                            <Mail className="w-3 h-3" />
                            {u.email}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right mr-4">
                          <p className="text-sm text-slate-600">
                            <span className="font-medium">{getUserPortfolios(u.user_id).length}</span> {t('common.portfolios')} · <span className="font-medium">{getUserAssetCount(u.user_id)}</span> {t('common.assets')}
                          </p>
                          <p className="text-xs text-slate-400 flex items-center justify-end gap-1">
                            <Calendar className="w-3 h-3" />
                            {t('admin.joined')} {formatDate(u.created_at)}
                          </p>
                        </div>
                        <Button
                          variant={u.is_admin ? "destructive" : "default"}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAdminRole(u.user_id, u.is_admin);
                          }}
                          disabled={u.user_id === user.user_id && u.is_admin}
                          className={u.is_admin ? '' : 'bg-[#0F766E] hover:bg-[#115E59]'}
                          data-testid={`toggle-admin-${u.user_id}`}
                        >
                          {u.is_admin ? (
                            <>
                              <ShieldOff className="w-4 h-4 mr-1" />
                              {t('admin.removeAdmin')}
                            </>
                          ) : (
                            <>
                              <Shield className="w-4 h-4 mr-1" />
                              {t('admin.makeAdmin')}
                            </>
                          )}
                        </Button>
                        {expandedUser === u.user_id ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Portfolios */}
                    {expandedUser === u.user_id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="border-t border-slate-200 bg-slate-50 p-4"
                      >
                        <h4 className="text-sm font-medium text-slate-700 mb-3">
                          {t('admin.userPortfolios')}
                        </h4>
                        {getUserPortfolios(u.user_id).length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {getUserPortfolios(u.user_id).map(portfolio => (
                              <div 
                                key={portfolio.id}
                                className="bg-white p-3 rounded-lg border border-slate-200 cursor-pointer hover:border-teal-300 transition-colors"
                                onClick={() => navigate(`/portfolio/${portfolio.id}`)}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-slate-900">{portfolio.name}</span>
                                  <Badge variant="outline">{portfolio.home_currency || 'USD'}</Badge>
                                </div>
                                <p className="text-sm text-slate-500 mt-1">
                                  {portfolio.assets?.length || 0} {t('common.assets')}
                                </p>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">{t('admin.noPortfoliosYet')}</p>
                        )}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default Admin;
